/* ============================================================================
   DATA — the PDP's product content.

   Catarina copy, price, sizes and fits are from the live wearfigs.com PDP
   (~/figs-products cache). The colours are the live PDP's own: its CORE and
   LIMITED EDITION groups in its order, named as it names them, each swatch on
   the site's ColorSwatch hex (read from the page's swatch styles, 2026-10-01).
   Espresso and Bonsai are the fall drop (pulled 2026-10-06), first in
   LIMITED EDITION with the site's NEW badge; Bonsai's ghost is its own
   W_O_GUIDE render, Espresso's the drop's embroidery render, both set in
   the same square as the rest.
   Photography is wearfigs.com's, up to seven shots per colour, vendored into
   assets/catarina/<slug>/ with a square card.webp and a cutout.webp — the
   card's model lifted off the studio backdrop (macOS Vision's subject mask),
   transparent and cropped to her — for the Color Gallery cards.

   Ghosts (Visual Color Compare only) are wearfigs.com's no-model Catarina
   renders — the embroidery preview's W_CATARINA_<COLOR>_W_O_GUIDE.png —
   transparent, cropped to one shared square so every colour lands in the
   same place. 16 of the 37 colours have one; the rest are drawn as the
   off-white ghost tinted to their hex.

   Shop the Look products are real FIGS styles matched to the Figma ghost
   imagery, with live price / size / colour data.
   ========================================================================== */

const shots = (slug, n) =>
  Array.from({ length: n }, (_, i) => `assets/catarina/${slug}/${String(i + 1).padStart(2, '0')}.webp`);
const look = (slug, n) =>
  Array.from({ length: n }, (_, i) => `assets/look/${slug}/${String(i + 1).padStart(2, '0')}.webp`);

/* wearfigs.com ColorSwatch hexes for every live Catarina colour, then the
   few Shop the Look colours Catarina doesn't come in. */
export const HEX = {
  black: '#000000', navy: '#403f6f', graphite: '#8d8d97', 'ceil-blue': '#5e7ec3',
  'royal-blue': '#095bdb', burgundy: '#692a2d', mauve: '#885157', charcoal: '#645b67',
  moss: '#56584c', 'dark-harbor': '#24323f', 'caribbean-blue': '#18455a', 'fight-club-pink': '#f7b1c8',
  'f-ing-pink': '#cb5b8a', amethyst: '#5b4f8f', 'berry-spice': '#954955', 'blue-horizon': '#007ab0',
  celery: '#c2bf74', creamsicle: '#fc892d', 'deep-purple': '#462639', 'deep-reef': '#133951',
  'deep-royal-blue': '#333e83', fireside: '#d94952', 'forest-green': '#264a48', 'harbor-grey': '#84949f',
  hibiscus: '#d93744', lagoon: '#008786', 'martini-olive': '#756539', 'off-white': '#efeadf',
  'optic-white': '#edf1ff', 'pop-red': '#d90a28', 'purple-haze': '#bfa0c1', 'rapid-response-pink': '#eb4398',
  sagewater: '#bad5c0', 'sea-mist': '#d7e494', seaglass: '#76a7ab', sienna: '#90403b',
  'smokey-taupe': '#7b6660', espresso: '#352120', bonsai: '#474e4d',
  'pulmonary-pink': '#ffb6c1', 'shocking-pink': '#f9216e', white: '#ffffff', cream: '#efeadf',
  'light-grey': '#c6c6c6',
  'grey-navy': 'linear-gradient(135deg, #c6c6c6 0 50%, #403f6f 50% 100%)',
  'surprise-me': '#dc143c', // the GIF's first frame — the swatch itself plays the Figma GIF
};

const NAMES = {
  'ceil-blue': 'Ceil Blue', 'royal-blue': 'Royal Blue', 'dark-harbor': 'Dark Harbor', 'caribbean-blue': 'Caribbean Blue',
  'fight-club-pink': 'Fight Club Pink', 'f-ing-pink': 'F*ing Pink', 'berry-spice': 'Berry Spice', 'blue-horizon': 'Blue Horizon',
  'deep-purple': 'Deep Purple', 'deep-reef': 'Deep Reef', 'deep-royal-blue': 'Deep Royal Blue', 'forest-green': 'Forest Green',
  'harbor-grey': 'Harbor Grey', 'martini-olive': 'Martini Olive', 'off-white': 'Off White', 'optic-white': 'Optic White',
  'pop-red': 'Pop Red', 'purple-haze': 'Purple Haze', 'rapid-response-pink': 'Rapid Response Pink', 'sea-mist': 'Sea Mist',
  'smokey-taupe': 'Smokey Taupe', 'pulmonary-pink': 'Pulmonary Pink', 'shocking-pink': 'Shocking Pink', 'light-grey': 'Light Grey',
  'grey-navy': 'Grey/Navy', 'surprise-me': 'Surprise Me',
};
const nameOf = (slug) => NAMES[slug] || slug.charAt(0).toUpperCase() + slug.slice(1);

/* Visual Color Compare's rails (Figma 1208:8610) run by shade, not core then
   A–Z, so the colours someone weighs against each other sit side by side.
   Families walk the colour wheel — neutrals, blues, teals, greens, orange,
   reds, roses and pinks, purples — each dark to light, placed by its OKLCH
   lightness and hue and checked against the photography (Caribbean Blue
   wears as a teal, Moss as a green, Mauve and Berry Spice as roses). Both
   products use it; a colour missing here goes to the end. */
export const SHADES = [
  ['black', 'espresso', 'charcoal', 'smokey-taupe', 'graphite', 'harbor-grey', 'off-white', 'optic-white'],
  ['dark-harbor', 'deep-reef', 'navy', 'deep-royal-blue', 'royal-blue', 'blue-horizon', 'ceil-blue'],
  ['caribbean-blue', 'lagoon', 'seaglass', 'sagewater'],
  ['forest-green', 'bonsai', 'moss', 'martini-olive', 'celery', 'sea-mist'],
  ['creamsicle'],
  ['burgundy', 'sienna', 'pop-red', 'hibiscus', 'fireside'],
  ['mauve', 'berry-spice', 'f-ing-pink', 'rapid-response-pink', 'fight-club-pink'],
  ['deep-purple', 'amethyst', 'purple-haze'],
];
const swatch = (slug, extra = {}) => ({ slug, name: nameOf(slug), hex: HEX[slug], ...extra });

/* Catarina colours also carry their ghost, gallery-card thumbnail and product
   video. Six colours have their own film on wearfigs.com; the others share
   the site's general Catarina film, and Charcoal and Moss have none — nor
   does Surprise Me, whose one photo is the box it ships in. */
const GHOSTS = new Set(['espresso', 'bonsai', 'black', 'burgundy', 'caribbean-blue', 'ceil-blue', 'charcoal', 'creamsicle', 'dark-harbor', 'deep-reef', 'graphite', 'lagoon', 'mauve', 'moss', 'navy', 'off-white', 'rapid-response-pink', 'royal-blue']);
const OWN_VIDEO = new Set(['black', 'graphite', 'ceil-blue', 'royal-blue', 'burgundy', 'mauve']);
const NO_VIDEO = new Set(['charcoal', 'moss', 'surprise-me']);
const catarina = (slug, extra = {}) => {
  const c = swatch(slug, extra);
  if (GHOSTS.has(slug)) Object.assign(c, { ghost: `assets/ghosts/${slug}.webp`, chip: `assets/ghosts/${slug}-chip.webp` });
  if (c.images) {
    c.card = `assets/catarina/${slug}/card.webp`;
    c.cutout = `assets/catarina/${slug}/cutout.webp`;
    if (!NO_VIDEO.has(slug)) c.video = `assets/catarina/videos/${OWN_VIDEO.has(slug) ? slug : 'catarina'}.mp4`;
  }
  return c;
};

export const SIZE_NAMES = {
  XXS: 'XX-Small', XS: 'X-Small', S: 'Small', M: 'Medium', L: 'Large', XL: 'X-Large',
  '2XL': '2X-Large', '3XL': '3X-Large', '4XL': '4X-Large', '5XL': '5X-Large', '6XL': '6X-Large',
};
const APPAREL_11 = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
const APPAREL_7 = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL'];
const MENS_10 = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];

export const CATARINA = {
  id: 'catarina',
  name: 'Catarina One-Pocket Scrub Top™',
  price: 42,
  url: 'https://www.wearfigs.com/products/womens-catarina-scrub-top',
  sizes: APPAREL_11,
  types: ['Regular', 'Petite'],
  fitTip: 'Model is 5’8” and is wearing an X-Small',
  colors: {
    core: [
      catarina('black', { images: shots('black', 7) }),
      catarina('navy', { images: shots('navy', 6) }),
      catarina('graphite', { images: shots('graphite', 6) }),
      catarina('ceil-blue', { images: shots('ceil-blue', 1) }),
      catarina('royal-blue', { images: shots('royal-blue', 5) }),
      catarina('burgundy', { images: shots('burgundy', 1) }),
      catarina('mauve', { images: shots('mauve', 6) }),
      catarina('charcoal', { images: shots('charcoal', 1) }),
      catarina('moss', { images: shots('moss', 5) }),
      catarina('dark-harbor', { images: shots('dark-harbor', 6) }),
      catarina('caribbean-blue', { images: shots('caribbean-blue', 7) }),
    ],
    limited: [
      catarina('espresso', { images: shots('espresso', 7), isNew: true }),
      catarina('bonsai', { images: shots('bonsai', 7), isNew: true }),
      catarina('fight-club-pink', { images: shots('fight-club-pink', 7) }),
      catarina('f-ing-pink', { images: shots('f-ing-pink', 7) }),
      catarina('amethyst', { images: shots('amethyst', 7) }),
      catarina('berry-spice', { images: shots('berry-spice', 7) }),
      catarina('blue-horizon', { images: shots('blue-horizon', 7) }),
      catarina('celery', { images: shots('celery', 7) }),
      catarina('creamsicle', { images: shots('creamsicle', 7) }),
      catarina('deep-purple', { images: shots('deep-purple', 7) }),
      catarina('deep-reef', { images: shots('deep-reef', 7) }),
      catarina('deep-royal-blue', { images: shots('deep-royal-blue', 7) }),
      catarina('fireside', { images: shots('fireside', 7) }),
      catarina('forest-green', { images: shots('forest-green', 7) }),
      catarina('harbor-grey', { images: shots('harbor-grey', 7) }),
      catarina('hibiscus', { images: shots('hibiscus', 7) }),
      catarina('lagoon', { images: shots('lagoon', 7) }),
      catarina('martini-olive', { images: shots('martini-olive', 7) }),
      catarina('off-white', { images: shots('off-white', 7) }),
      catarina('optic-white', { images: shots('optic-white', 7) }),
      catarina('pop-red', { images: shots('pop-red', 7) }),
      catarina('purple-haze', { images: shots('purple-haze', 7) }),
      catarina('rapid-response-pink', { images: shots('rapid-response-pink', 7) }),
      catarina('sagewater', { images: shots('sagewater', 7) }),
      catarina('sea-mist', { images: shots('sea-mist', 7) }),
      catarina('seaglass', { images: shots('seaglass', 7) }),
      catarina('sienna', { images: shots('sienna', 7) }),
      catarina('smokey-taupe', { images: shots('smokey-taupe', 7) }),
      /* Figma 1199:151588 — a final swatch that plays the Figma GIF; selecting
         it names the colour and keeps the current photography, as the frame shows */
      catarina('surprise-me', {
        surprise: true, images: shots('surprise-me', 1),
        imageAlt: 'Surprise Me: a FIGS box covered in stickers — the color inside is a surprise',
      }),
    ],
  },
};

/* ============================================================================
   ISABEL — the Scrub Pants PDP (pants.html · Figma 877:187323). Copy, price
   and palette are the live wearfigs.com PDP's (womens-isabel-scrub-pants,
   2026-10-05): eleven CORE and fifteen LIMITED EDITION colours in its order,
   six or seven shots each, vendored into assets/isabel/<slug>/ with a square
   card.webp. wearfigs.com shares one Isabel film across the palette
   (Fireside has none). Sizes run to 6XL like the Figma size drawer, with the
   site's Regular / Petite / Tall lengths.

   wearfigs.com publishes no no-model Isabel render (pants aren't embroidered,
   so there's no W_O_GUIDE ghost), so Visual Color Compare uses the Figma's
   navy ghost of the pant — the one real render — and every other colour is
   that ghost recoloured to its swatch, shading and drawstring kept.
   ========================================================================== */
const ISABEL_SHOTS = {
  black: 7, navy: 7, graphite: 6, 'ceil-blue': 7, 'royal-blue': 6, burgundy: 7, mauve: 7, charcoal: 7, moss: 7,
  'dark-harbor': 7, 'caribbean-blue': 7, amethyst: 7, 'berry-spice': 6, 'blue-horizon': 7, celery: 7, 'deep-purple': 7,
  'f-ing-pink': 7, 'fight-club-pink': 7, fireside: 7, 'harbor-grey': 7, hibiscus: 7, 'martini-olive': 7, 'optic-white': 7,
  sagewater: 6, sienna: 7, 'smokey-taupe': 6,
};
const isabel = (slug) => ({
  ...swatch(slug),
  images: Array.from({ length: ISABEL_SHOTS[slug] }, (_, i) => `assets/isabel/${slug}/${String(i + 1).padStart(2, '0')}.webp`),
  card: `assets/isabel/${slug}/card.webp`,
  cutout: `assets/isabel/${slug}/cutout.webp`,
  ghost: `assets/isabel-ghosts/${slug}.webp`,
  chip: `assets/isabel-ghosts/${slug}-chip.webp`,
  ...(slug === 'fireside' ? {} : { video: 'assets/isabel/isabel.mp4' }),
});

export const ISABEL = {
  id: 'isabel',
  name: 'Isabel High Waisted Wide-Leg Scrub Pants',
  plainName: 'Isabel High Waisted Wide-Leg Scrub Pants',
  shortName: 'Isabel',
  price: 58,
  url: 'https://www.wearfigs.com/products/womens-isabel-scrub-pants',
  sizes: APPAREL_11,
  types: ['Regular', 'Petite', 'Tall'],
  typeLabel: 'Length',
  fitTip: 'Model is 5’8” and is wearing an X-Small',
  colors: {
    core: ['black', 'navy', 'graphite', 'ceil-blue', 'royal-blue', 'burgundy', 'mauve', 'charcoal', 'moss', 'dark-harbor', 'caribbean-blue'].map(isabel),
    limited: ['amethyst', 'berry-spice', 'blue-horizon', 'celery', 'deep-purple', 'f-ing-pink', 'fight-club-pink', 'fireside',
      'harbor-grey', 'hibiscus', 'martini-olive', 'optic-white', 'sagewater', 'sienna', 'smokey-taupe'].map(isabel),
  },
};
Object.assign(CATARINA, { plainName: 'Catarina One-Pocket Scrub Top', shortName: 'Catarina', typeLabel: 'Type' });

/* ============================================================================
   PRESET BUNDLES — set.html (Figma 1647:80121, "Preset Bundles"). A bundle is
   a fixed set of products sold as one, at one price, in one colour: the page
   is a template over its `components`, so a three-piece kit is a third entry,
   not new markup. The size drawer draws a Kit Product Card, sizes and the
   second variant per component; the triptych keeps the Fit card and gives
   every component a card; the reviews drawer gives every component a tab.

   The page picks its bundle from ?set= (wide-leg by default). Three are the
   live wearfigs.com kits behind the site's Straight-Leg / Jogger / Wide-Leg
   toggles (pulled 2026-10-06/07): their prices, descriptions, colours (each
   kit's own list, order and hex — no NEW badges), components, sizes, lengths,
   Product Details and Wear It With rows. The fourth, ?set=espresso, is a
   three-piece bundle built for the Figma template (no such kit is live):
   Catarina + Isabel + the Sydney Scrub Vest, all three in Espresso, priced
   as the sum of their live prices, as the live kits are.

   ?set=mens is the live men's set (wearfigs.com/kits/mens-the-set, pulled
   2026-10-21): the Leon top and Tansen jogger. mens-set.html opens on it
   (<html data-set>), with men's crumbs and the men's size charts.

   The set toggles are off (`toggles: false` on every kit, 2026-10-07): the
   Scrub Sets PLP (14-plp-scrub-sets) links every set card here, to the
   default Wide-Leg Set. SET_TOGGLES and bundle.js still draw them for a kit
   that turns them back on.

   Imagery is per component (assets/set/<component>/<colour>/: shots 1, 3, 4
   of the kit's four — the site skips 2, an alternate — and the set ghost),
   and per kit for the first plate (assets/set/plates/<kit>/<colour>.webp:
   every garment's ghost trimmed and set at Navy's size and height, so a
   colour change never jumps).
   ========================================================================== */
const SHIPPING = [
  'FIGS offers free U.S. shipping on orders over $50 and free returns on all eligible orders.',
  'Eligible items can be returned within 30 days of purchase for a refund. U.S. customers may request an exchange or return items for store credit up to 60 days from the purchase date. Final sale, embroidered, and specified non-returnable items cannot be returned.',
];
const FIONX_CARE = (care) => [
  'Features our proprietary FIONx™ fabric with four-way stretch, moisture-wicking, anti-wrinkle and ridiculously soft properties',
  'Features Silvadur™ antimicrobial technology, which provides odor protection, inhibits the growth of bacteria, yeast, algae, and other microorganisms that cause odor and fabric damage, and increases the lifespan and durability of the product',
  '72% Polyester · 21% Rayon · 7% Spandex',
  care,
];
/* the men's styles' Fabric & Care, as their PDPs list it */
const MENS_FIONX = [
  'FIONx™ fabric: Lightweight, breathable, and ridiculously soft, with moisture-wicking and super durable four-way stretch properties.',
  'Silvadur™ antimicrobial technology: Provides odor protection, inhibits bacteria growth, and extends product lifespan.',
  '72% Polyester · 21% Rayon · 7% Spandex', 'Wash cold, inside-out · Tumble dry low',
];
const PANT_LENGTHS = (petite, regular, tall) => ['Petite, Regular, and Tall pants differ only in length',
  `Petite length: ${petite}”`, `Regular length: ${regular}”`, `Tall length: ${tall}”`];

/* every product a bundle can hold — the kit pages' and Product Details'
   copy; `card` is its triptych card's lines above DETAILS (Figma 1594:89626,
   from SITE WORKSHOP slide 23). `price` is
   the garment's live price: a set adds one bag line per garment, and the
   kits' prices are these summed. */
const component = (c) => ({
  ghost: (slug) => `assets/set/${c.id}/${slug}/ghost.webp`,
  image: (slug) => `assets/set/${c.id}/${slug}/1.webp`,
  shots: (slug) => [1, 2, 3].map((n) => `assets/set/${c.id}/${slug}/${n}.webp`),
  ...c,
});
export const SET_COMPONENTS = {
  catarina: component({
    id: 'catarina', role: 'Top', name: 'Catarina Scrub Top™', plainName: 'Catarina Scrub Top', shortName: 'Catarina', price: 42,
    sizes: APPAREL_11, types: ['Regular', 'Petite'], typeLabel: 'Type', chart: 'tops', embroidery: true,
    reviews: 58710, page: './',
    fit: ['Claire is 5’8.5” / Wearing XS', 'Beck is 5’3” / Wearing XS Petite', 'Madeline is 5’10” / Wearing S',
      'Taylor is 5’8.5” / Wearing M', 'Maya is 5’10” / Wearing L', 'Sarah is 5’7” / Wearing XL',
      'Megan is 5’9” / Wearing 2XL', 'Cora is 5’10” / Wearing 3XL', 'Lena is 5’6” / Wearing 5XL'],
    features: ['Select colors also available in maternity', 'One chest pocket', 'Modern V-neck', 'Darted back',
      'Side-slit hem', 'Engineered with Technical Comfort™'],
    fabric: FIONX_CARE('Wash cold inside-out, tumble dry low.'),
    shopMore: ['Core Scrubs', 'Scrub Tops'],
    card: ['One-Pocket', 'One chest pocket', 'Modern V-neck'],
  }),
  isabel: component({
    id: 'isabel', role: 'Pant', name: 'Isabel High-Rise Wide-Leg Scrub Pant', plainName: 'Isabel High-Rise Wide-Leg Scrub Pant', price: 58,
    shortName: 'Isabel', sizes: APPAREL_7, types: ['Regular', 'Petite', 'Tall'], typeLabel: 'Length', chart: 'pants',
    embroidery: false, reviews: 4540, page: './pants.html',
    fit: ['Model is 5’10” / Wearing XS Tall'],
    features: ['Select colors also available in a mid rise with a ruched waistband.', 'Two front slash pockets',
      'One interior security pocket', 'One patch pocket', 'One pocket within patch pocket', 'One interior pen pocket',
      'Two back pockets', 'Wide leg fit', 'Yoga waistband with adjustable drawcord', 'Clean hem',
      ...PANT_LENGTHS(29, 31, 33), 'Engineered with Technical Comfort™'],
    fabric: FIONX_CARE('Wash cold inside-out, tumble dry low'),
    shopMore: ['Scrub Pants', 'Scrubs'],
    card: ['8 Pockets', 'Wide leg fit', 'Yoga waistband with adjustable drawcord'],
  }),
  kade: component({
    id: 'kade', role: 'Pant', name: 'Kade Straight-Leg Scrub Pant™', plainName: 'Kade Straight-Leg Scrub Pant', shortName: 'Kade', price: 52,
    sizes: APPAREL_11, types: ['Regular', 'Petite', 'Tall'], typeLabel: 'Length', chart: 'pants', embroidery: false,
    reviews: 16414, page: 'https://www.wearfigs.com/products/womens-kade-cargo-scrub-pants',
    fit: ['Joyce is 5’9” / Wearing XS Regular', 'Madeline is 5’10” / Wearing S Tall', 'Skye is 5’9” / Wearing M Regular',
      'Sarah is 5’7” / Wearing XL Regular', 'Carly is 5’8” / Wearing 3XL Regular', 'Tess is 5’5” / Wearing 4XL Regular'],
    features: ['This is a mid rise style.', 'Select colors also available in high rise and maternity', 'Two trouser pockets',
      'Two side cargo pockets', 'Straight leg', 'Yoga waistband with adjustable drawcord',
      'Flattering back yoke for a more fitted look', 'Side slits at ankles', ...PANT_LENGTHS(29, 31, 33),
      'Engineered with Technical Comfort™'],
    fabric: FIONX_CARE('Wash cold inside-out, tumble dry low.'),
    shopMore: ['Scrub Pants'],
    card: ['4 Pockets', 'Straight leg fit', 'Yoga waistband with adjustable drawcord'],
  }),
  zamora: component({
    id: 'zamora', role: 'Pant', name: 'Zamora Jogger Scrub Pant™', plainName: 'Zamora Jogger Scrub Pant', shortName: 'Zamora', price: 52,
    sizes: APPAREL_11, types: ['Regular', 'Petite', 'Tall'], typeLabel: 'Length', chart: 'pants', embroidery: false,
    reviews: 40846, page: 'https://www.wearfigs.com/products/womens-zamora-scrub-pants',
    fit: ['Madeline is 5’10” / Wearing S Tall', 'Skye is 5’9” / Wearing M Regular', 'Sarah is 5’7” / Wearing XL Regular',
      'Megan is 5’9” / Wearing 2XL Regular', 'Cora is 5’10” / Wearing 3XL Regular', 'Devina is 5’9” / Wearing 4XL Regular'],
    features: ['Select colors also available in high rise with yoga waistband, mid rise with a ruched waistband and maternity',
      'One side cargo pocket with semi auto-lock zipper technology', 'One interior security pocket', 'Two deep trouser pockets',
      'Two back patch pockets', 'Yoga waistband with adjustable drawcord', 'Ribbed ankle cuffs', ...PANT_LENGTHS(26.5, 28.5, 30.5),
      'Engineered with Technical Comfort™'],
    fabric: FIONX_CARE('Wash cold inside-out, tumble dry low.'),
    shopMore: ['Core Scrubs', 'Scrub Pants'],
    card: ['6 Pockets', 'Jogger fit with ribbed cuffs', 'Yoga waistband with adjustable drawcord'],
  }),
  'sydney-vest': component({
    /* $88: the Espresso Set's $188 less the Catarina and Isabel */
    id: 'sydney-vest', role: 'Layer', name: 'Sydney Scrub Vest', plainName: 'Sydney Scrub Vest', shortName: 'Sydney Vest', price: 88,
    sizes: APPAREL_11, types: ['Regular'], typeLabel: 'Type', chart: 'tops', embroidery: true,
    reviews: 97, page: 'https://www.wearfigs.com/products/womens-sydney-vest?color=Espresso',
    fit: ['Model is 5’10” / Wearing XS', 'Classic fit. Runs true to size.'],
    features: ['Two on-seam drop pockets', 'Two hand pockets', 'One interior dump pocket', 'One interior security pocket',
      'Stand collar', 'Elastic hem', 'Engineered with Technical Comfort™'],
    fabric: ['FIONx™ fabric: Lightweight, breathable, and ridiculously soft, with moisture-wicking and super durable four-way stretch properties.',
      'Silvadur™ antimicrobial technology: Provides odor protection, inhibits bacteria growth, and extends product lifespan.',
      '72% Polyester · 21% Rayon · 7% Spandex', 'Body lining: 100% Polyester', 'Wash cold, inside-out · Tumble dry low'],
    shopMore: ['Outerwear'],
    card: ['6 Pockets', 'Stand collar', 'Elastic hem'],
  }),
  /* the men's set (wearfigs.com/kits/mens-the-set): its PDPs' copy, fit
     models and review counts as the kit page shows them (2026-10-21) */
  leon: component({
    id: 'leon', role: 'Top', gender: 'men', name: 'Leon Scrub Top™', plainName: 'Leon Scrub Top', shortName: 'Leon', price: 42,
    sizes: MENS_10, types: ['Regular'], typeLabel: 'Type', chart: 'mens-tops', embroidery: true,
    reviews: 12985, page: 'https://www.wearfigs.com/products/mens-leon-scrub-top',
    fit: ['Alexander is 6’4” / Wearing L', 'Tom is 6’4” / Wearing 3XL'],
    features: ['One chest pocket', 'One hidden patch pocket', 'One pen sleeve', 'Modern V-neck', 'Engineered with Technical Comfort™'],
    fabric: MENS_FIONX,
    shopMore: ['Scrub Tops', 'Scrubs'],
    card: ['3 Pockets', 'Classic fit', 'Modern V-neck'],
  }),
  tansen: component({
    id: 'tansen', role: 'Pant', gender: 'men', name: 'Tansen Jogger Scrub Pant™', plainName: 'Tansen Jogger Scrub Pant', shortName: 'Tansen', price: 52,
    sizes: MENS_10, types: ['Regular', 'Short', 'Tall'], typeLabel: 'Length', chart: 'mens-pants', embroidery: false,
    reviews: 6895, page: 'https://www.wearfigs.com/products/mens-tansen-scrub-pants',
    fit: ['Joseph is 6’2” / Wearing M Regular', 'Tom is 6’4” / Wearing 3XL Regular'],
    features: ['Two trouser pockets', 'Two back welt pockets', 'One interior security pocket',
      'Elastic waistband with adjustable drawcord', 'Ribbed ankle cuffs', 'Short, Regular, and Tall pants differ only in length',
      'Short length: 27”', 'Regular length: 29”', 'Tall length: 31”', 'Engineered with Technical Comfort™'],
    fabric: MENS_FIONX,
    shopMore: ['Scrub Pants', 'Scrubs'],
    card: ['5 Pockets', 'Slim fit with ribbed ankle cuffs', 'Elastic waistband with adjustable drawcord'],
  }),
};

/* a kit's colour list: [slug, hex, NEW?, name] in the kit page's order. The
   set page shows no NEW dots (2026-10-08): they're the single-product PDPs'
   alone, so NEW? is always false here. */
const kitColors = (slugs) => slugs.map((slug) => [slug, slug === 'dark-acai' ? '#8a3867' : HEX[slug], false, slug === 'dark-acai' ? 'Dark Açaí' : nameOf(slug)]);
const CORE_11 = ['black', 'navy', 'graphite', 'ceil-blue', 'royal-blue', 'burgundy', 'mauve', 'charcoal', 'moss', 'dark-harbor', 'caribbean-blue'];

/* a kit's gallery (Figma 1594:89624): on model first, the set's ghost plate
   last. `lead` puts a colour's own shots up front (the Wide-Leg Set's navy
   set-on-model, pant and waistband), each dropped from where it'd repeat. */
const kit = (k) => {
  const parts = k.components.map((id) => SET_COMPONENTS[id]);
  const color = ([slug, hex, isNew, name]) => {
    const shots = parts.flatMap((c) => c.shots(slug).map((src, i) => [src, `${c.plainName} in ${name}, on model, ${i + 1} of 3`]));
    const lead = k.lead?.[slug] || [];
    const plate = [`assets/set/plates/${k.plate}/${slug}.webp`,
      `${k.name} in ${name}: the ${parts.map((c) => c.shortName).join(', ').replace(/, ([^,]*)$/, ' and $1')} side by side`];
    const all = [...lead, ...shots.filter(([src]) => !lead.some(([l]) => l === src)), plate];
    return { slug, name, hex, isNew, images: all.map(([src]) => src), alts: all.map(([, alt]) => alt) };
  };
  return {
    kit: true, plainName: k.name, shipping: SHIPPING, ...k,
    components: parts,
    reviews: parts.reduce((n, c) => n + c.reviews, 0),
    colors: { core: kitColors(k.core).map(color), limited: kitColors(k.limited).map(color) },
  };
};

/* the live kits' Wear It With rows (names, colours and prices as listed) */
const WEAR = {
  salta: { name: 'Salta Seamless Long-Sleeve Underscrub', colorName: 'Graphite', price: '$58.00', image: 'assets/set/picks/salta.webp' },
  sydney: { name: 'Sydney Scrub Jacket', colorName: 'Black', price: '$98.00', image: 'assets/set/picks/sydney.webp' },
  archtek: { name: 'FIGS | ArchTek® Compression Socks 20-30 mmHg', colorName: 'Black', price: '$36.00', image: 'assets/set/picks/archtek.webp' },
  nb3447: { name: 'FIGS | New Balance 3447', colorName: 'Cream', price: '$158.00', image: 'assets/set/picks/nb3447.webp' },
  nb327: { name: 'FIGS | New Balance 327', colorName: 'Black', price: '$128.00', image: 'assets/set/picks/nb327-black.webp' },
};
/* the men's set's Wear It With row, as listed (2026-10-21) */
const MENS_WEAR = [
  { name: 'Makato Seamless Long-Sleeve Underscrub', colorName: 'Graphite', price: '$58.00', image: 'assets/set/picks/makato.webp' },
  { name: 'Cobaki Scrub Jacket', colorName: 'Black', price: '$98.00', image: 'assets/set/picks/cobaki.webp' },
  { name: 'FIGS | ArchTek® Compression Socks 15-20 mmHg', colorName: 'Charcoal', price: '$28.00', image: 'assets/set/picks/archtek-15-20.webp' },
  { name: 'FIGS | New Balance 327', colorName: 'Black', price: '$128.00', image: 'assets/set/picks/nb327-black-ghost.webp' },
  { name: 'The Shift Backpack', colorName: 'Black', price: '$148.00', image: 'assets/set/picks/shift-backpack.webp' },
];

/* Wear it with — the set page's cross-sell card (Complete the Set,
   877:194945): the Sydney Scrub Jacket, the live kits' Wear It With pick
   ($98). Shown in the page's colour where wearfigs.com has its no-model
   render (W_SYDNEY_JACKET_<COLOR>_W_O_GUIDE), otherwise in Black — the
   colour the live row shows. */
const SYDNEY_RENDERS = new Set(['black', 'navy', 'graphite', 'ceil-blue', 'royal-blue', 'burgundy', 'mauve', 'charcoal', 'dark-harbor', 'caribbean-blue', 'espresso']);
const SYDNEY_XSELL = [{
  id: 'sydney-jacket', name: 'Sydney Scrub Jacket', plainName: 'Sydney Scrub Jacket', shortName: 'Sydney', price: 98,
  sizes: APPAREL_11, types: ['Regular'], typeLabel: 'Type', fitTip: '',
  url: 'https://www.wearfigs.com/products/womens-sydney-scrub-jacket',
  shown: (c) => (SYDNEY_RENDERS.has(c.slug) ? c : { slug: 'black', name: 'Black' }),
  image: (slug) => `assets/set/xsell/sydney-jacket/${SYDNEY_RENDERS.has(slug) ? slug : 'black'}.webp`,
  model: (slug) => `assets/set/xsell/sydney-jacket/model/${SYDNEY_RENDERS.has(slug) ? slug : 'black'}.webp`, // the bag's photo
}];
/* the men's set's: the Cobaki Scrub Jacket, its Wear It With jacket ($98),
   by the same rule — its no-model render (M_COBAKI_…_W_O_GUIDE), else Black */
const COBAKI_RENDERS = new Set(['black', 'navy', 'royal-blue', 'burgundy', 'charcoal', 'moss', 'caribbean-blue', 'bonsai', 'espresso']);
const COBAKI_XSELL = [{
  id: 'cobaki-jacket', name: 'Cobaki Scrub Jacket', plainName: 'Cobaki Scrub Jacket', shortName: 'Cobaki', price: 98,
  sizes: MENS_10, types: ['Regular'], typeLabel: 'Type', fitTip: '', chart: 'mens-tops',
  url: 'https://www.wearfigs.com/products/mens-cobaki-scrub-jacket',
  shown: (c) => (COBAKI_RENDERS.has(c.slug) ? c : { slug: 'black', name: 'Black' }),
  image: (slug) => `assets/set/xsell/cobaki-jacket/${COBAKI_RENDERS.has(slug) ? slug : 'black'}.webp`,
  model: (slug) => `assets/set/xsell/cobaki-jacket/model/${COBAKI_RENDERS.has(slug) ? slug : 'black'}.webp`,
}];

/* the site's set toggles, in its order */
export const SET_TOGGLES = [
  { id: 'straight-leg', label: 'Straight-Leg Set' },
  { id: 'jogger', label: 'Jogger Set' },
  { id: 'wide-leg', label: 'Wide-Leg Set' },
];


/* SHOP THE LOOK + EDITORIAL for a set — Figma 1594:89620 (from SITE WORKSHOP
   slide 23 and the Dropbox SCRUB SET PDP folder): Head to Toe with the Wide
   Leg Set — Sydney Scrub Jacket, Salta underscrub, the A Very Large Needle
   tote and NB 327s, names and prices from wearfigs.com (2026-10-07). Shared
   with LOOK: the NB 327. */
const NB327_LOOK = {
  id: 'nb327',
  name: 'FIGS | New Balance 327',
  shortName: 'New Balance 327',
  color: 'grey-navy',
  price: 128,
  url: 'https://www.wearfigs.com/products/unisex-figs-new-balance-327-shoes?color=Grey%2FNavy',
  image: 'assets/figma/stl-sneaker.webp',
  imageMobile: 'assets/figma/stl-sneaker-mobile.webp',
  fit: 'sneaker',
  gallery: look('nb327-grey-navy', 4),
  sizes: ['M4/W5.5', 'M4.5/W6', 'M5/W6.5', 'M5.5/W7', 'M6/W7.5', 'M6.5/W8', 'M7/W8.5', 'M7.5/W9', 'M8/W9.5',
    'M8.5/W10', 'M9/W10.5', 'M9.5/W11', 'M10/W11.5', 'M10.5/W12', 'M11/W12.5', 'M11.5/W13', 'M12/W13.5', 'M13/W14.5'],
  types: ['D-Standard'],
  typeLabel: 'Width',
  colors: {
    core: [],
    limited: ['grey-navy', 'black', 'white', 'cream', 'light-grey', 'mauve', 'caribbean-blue',
      'fight-club-pink', 'amethyst', 'blue-horizon', 'deep-royal-blue'].map((s) => swatch(s)),
  },
};
const SET_LOOK = {
  sydney: {
    id: 'sydney-jacket', name: 'Sydney Scrub Jacket', shortName: 'Sydney Scrub Jacket', color: 'navy', price: 98,
    url: 'https://www.wearfigs.com/products/womens-sydney-scrub-jacket?color=Navy',
    image: 'assets/set/wide-leg/wide-leg-stl-sydney.webp', fit: 'contain', gallery: ['assets/set/wide-leg/wide-leg-stl-sydney.webp'], galleryOnWhite: true,
    sizes: APPAREL_11, types: ['Regular'],
    colors: {
      core: ['black', 'navy', 'graphite', 'ceil-blue', 'royal-blue', 'burgundy', 'mauve', 'charcoal', 'moss', 'dark-harbor', 'caribbean-blue'].map((s) => swatch(s)),
      limited: ['espresso', 'celery', 'deep-purple', 'deep-reef', 'deep-royal-blue', 'lagoon'].map((s) => swatch(s)),
    },
  },
  salta: {
    id: 'salta', name: 'Salta Seamless Long-Sleeve Underscrub', shortName: 'Salta Seamless Long-Sleeve Underscrub', color: 'navy', price: 58,
    url: 'https://www.wearfigs.com/products/womens-salta-under-scrubs?color=Navy',
    image: 'assets/set/wide-leg/wide-leg-stl-salta.webp', fit: 'contain', gallery: ['assets/set/wide-leg/wide-leg-stl-salta.webp'], galleryOnWhite: true,
    sizes: APPAREL_11, types: ['Regular'],
    colors: {
      core: ['black', 'navy', 'graphite', 'ceil-blue', 'royal-blue', 'burgundy', 'mauve', 'moss', 'dark-harbor', 'caribbean-blue'].map((s) => swatch(s)),
      limited: ['espresso', 'deep-reef', 'hibiscus', 'martini-olive', 'optic-white'].map((s) => swatch(s)),
    },
  },
  tote: {
    id: 'avln-tote', name: 'A Very Large Needle Detachable Strap Tote', shortName: 'A Very Large Needle Detachable Strap Tote', color: 'black', price: 48,
    url: 'https://www.wearfigs.com/products/unisex-a-very-large-needle-detachable-strap-tote?color=Black',
    image: 'assets/set/wide-leg/wide-leg-stl-tote.webp', fit: 'contain', gallery: ['assets/set/wide-leg/wide-leg-stl-tote.webp'], galleryOnWhite: true,
    sizes: ['One Size'], types: ['Regular'],
    colors: {
      core: ['black'].map((s) => swatch(s)),
      limited: ['dark-harbor', 'espresso', 'blue-horizon', 'creamsicle', 'deep-purple', 'f-ing-pink', 'forest-green', 'martini-olive', 'purple-haze', 'sagewater', 'seaglass'].map((s) => swatch(s)),
    },
  },
  nb327: { ...NB327_LOOK, shortName: 'FIGS | New Balance 327' },
};

export const KITS = {
  'wide-leg': kit({
    id: 'wide-leg', name: 'The Wide-Leg Set', shortName: 'Wide-Leg Set', price: 100, plate: 'wide-leg',
    url: 'https://www.wearfigs.com/kits/womens-the-wide-leg-set-black', toggles: false,
    description: 'Go wide with on-the-pulse appeal. The Isabel High-Rise Wide-Leg Scrub Pant features a roomy silhouette with eight pockets and a super comfy waistband.',
    /* the Product Form's line on what's in it (Figma 1678:96985) */
    contents: 'The Catarina and The Isabel Wide Leg',
    components: ['catarina', 'isabel'],
    core: CORE_11,
    limited: ['espresso', 'bonsai', 'dark-acai', 'berry-spice', 'blue-horizon', 'celery', 'f-ing-pink', 'hibiscus',
      'martini-olive', 'optic-white', 'sagewater', 'sienna'],
    tri: { catarina: 'assets/set/wide-leg/wide-leg-tri-catarina.webp', isabel: 'assets/set/wide-leg/wide-leg-tri-isabel.webp' },
    lead: {
      navy: [
        ['assets/set/wide-leg/wide-leg-model-navy.webp', 'The Wide-Leg Set in Navy, on model: the Catarina top and Isabel wide-leg pant'],
        ['assets/set/isabel/navy/1.webp', 'Isabel High-Rise Wide-Leg Scrub Pant in Navy, on model'],
        ['assets/set/wide-leg/wide-leg-waist-navy.webp', 'The Isabel’s yoga waistband and drawcord, in Navy'],
      ],
    },
    story: {
      label: 'The Wide-Leg Set story',
      large: {
        src: 'assets/set/wide-leg/wide-leg-edit-large.webp', alt: 'A nurse in the Caribbean Blue Catarina top and Isabel wide-leg pant',
        /* the slide's callouts either side of the model, as a percentage of the plate */
        callouts: [{ text: 'The Catarina —', right: 62.5, top: 36 }, { text: '— The Isabel', left: 60, top: 62 }],
      },
      cards: [
        /* the Dropbox GIFs, as animated WebP looping from the Figma frame; `src` is the still Reduced shows */
        { anim: 'assets/set/wide-leg/wide-leg-edit-room-anim.webp', src: 'assets/set/wide-leg/wide-leg-edit-room.webp', alt: 'A nurse in the black Wide-Leg Set, a tablet under her arm', label: 'Room Where You Need It' },
        { anim: 'assets/set/wide-leg/wide-leg-edit-pockets-anim.webp', src: 'assets/set/wide-leg/wide-leg-edit-pockets.webp', alt: 'Trauma shears, a syringe and a pager balanced on a clinic tray', label: 'Pockets For Everything' },
        { anim: 'assets/set/wide-leg/wide-leg-edit-yoga-anim.webp', src: 'assets/set/wide-leg/wide-leg-edit-yoga.webp', alt: 'The graphite Wide-Leg Set from behind', label: 'Yoga Waistband — Super Comfy' },
      ],
      text: 'The Wide Leg Set is in our core collection — here for you 24/7.',
    },
    look: {
      title: 'with the Wide Leg Set',
      image: 'assets/set/wide-leg/wide-leg-look-navy.webp',
      alt: 'The look: a navy jacket over the navy Wide-Leg Set, with a canvas tote, a stethoscope and New Balance sneakers',
      items: [SET_LOOK.sydney, SET_LOOK.salta, SET_LOOK.tote, SET_LOOK.nb327],
    },
    picks: [WEAR.salta, WEAR.sydney, WEAR.archtek, WEAR.nb3447],
  }),
  'straight-leg': kit({
    id: 'straight-leg', name: 'The Straight-Leg Set', shortName: 'Straight-Leg Set', price: 94, plate: 'straight-leg',
    url: 'https://www.wearfigs.com/kits/womens-the-set-kade', toggles: false,
    description: 'Our best-selling scrub top and scrub pants that pack a lot of style and pockets.',
    contents: 'The Catarina One-Pocket Scrub Top and Kade Straight-Leg scrub pants together, at last.',
    components: ['catarina', 'kade'],
    core: CORE_11,
    limited: ['espresso', 'bonsai', 'dark-acai', 'amethyst', 'berry-spice', 'blue-horizon', 'celery', 'f-ing-pink', 'fireside',
      'harbor-grey', 'hibiscus', 'martini-olive', 'optic-white', 'sagewater', 'sea-mist', 'sienna'],
    tri: { catarina: 'assets/set/wide-leg/wide-leg-tri-catarina.webp', kade: 'assets/set/tri/kade-navy.webp' },
    picks: [WEAR.salta, WEAR.sydney, WEAR.archtek, WEAR.nb327],
  }),
  jogger: kit({
    id: 'jogger', name: 'The Jogger Set', shortName: 'Jogger Set', price: 94, plate: 'jogger',
    url: 'https://www.wearfigs.com/kits/womens-the-set', toggles: false,
    description: 'Our best-selling scrub top and super functional, jogger-style scrub pants.',
    contents: 'The Catarina One-Pocket Scrub Top and Zamora Jogger scrub pants together, at last.',
    components: ['catarina', 'zamora'],
    core: CORE_11,
    limited: ['espresso', 'bonsai', 'dark-acai', 'amethyst', 'berry-spice', 'blue-horizon', 'creamsicle', 'deep-purple',
      'deep-reef', 'deep-royal-blue', 'f-ing-pink', 'fight-club-pink', 'fireside', 'forest-green', 'hibiscus', 'lagoon',
      'martini-olive', 'optic-white', 'pop-red', 'purple-haze', 'rapid-response-pink', 'sea-mist', 'seaglass', 'sienna'],
    tri: { catarina: 'assets/set/wide-leg/wide-leg-tri-catarina.webp', zamora: 'assets/set/tri/zamora-espresso.webp' },
    /* the live Jogger Set page shows no Wear It With row (2026-10-07): the
       Straight-Leg Set's, which shares its top */
    picks: [WEAR.salta, WEAR.sydney, WEAR.archtek, WEAR.nb327],
  }),
  espresso: kit({
    id: 'espresso', name: 'The Espresso Set', shortName: 'Espresso Set', price: 188, plate: 'espresso',
    url: null, toggles: false, color: 'espresso',
    /* a limited colour, not Core: Why You'll Love This links to the colour */
    cta: 'Shop Espresso',
    description: 'Our best-selling scrub top, our most-likely-to-sell-out wide-leg scrub pants and the six-pocket Sydney Vest — all in Espresso.',
    contents: 'The Catarina One-Pocket Scrub Top, Isabel Wide-Leg scrub pants and Sydney Scrub Vest together, all in Espresso.',
    components: ['catarina', 'isabel', 'sydney-vest'],
    core: [],
    limited: ['espresso'],
    tri: { catarina: 'assets/set/tri/catarina-espresso.webp', isabel: 'assets/set/tri/isabel-espresso.webp', 'sydney-vest': 'assets/set/tri/vest-espresso.webp' },
    /* the rest of the live Espresso collection */
    picks: [
      { name: 'Salta Seamless Long-Sleeve Underscrub', colorName: 'Ombre Espresso', price: '$58.00', image: 'assets/set/picks/salta-espresso.webp' },
      { name: 'Bouclé Jacket', colorName: 'Espresso', price: '$128.00', image: 'assets/set/picks/boucle-espresso.webp' },
      { name: 'Casma Three-Pocket Scrub Top', colorName: 'Espresso', price: '$42.00', image: 'assets/set/picks/casma-espresso.webp' },
      { name: 'Zamora Jogger Scrub Pant', colorName: 'Espresso', price: '$52.00', image: 'assets/set/picks/zamora-espresso.webp' },
    ],
  }),
  /* the live men's set: no editorial or Shop the Look on wearfigs.com, so
     the page has neither */
  mens: kit({
    id: 'mens', name: 'The Jogger Set', shortName: 'Jogger Set', price: 94, plate: 'mens', gender: 'men',
    url: 'https://www.wearfigs.com/kits/mens-the-set', toggles: false,
    description: 'Our best-selling scrub top and super functional, jogger-style scrub pants.',
    contents: 'The Leon Scrub Top and Tansen Jogger scrub pants together, at last.',
    components: ['leon', 'tansen'],
    core: ['black', 'navy', 'graphite', 'ceil-blue', 'royal-blue', 'burgundy', 'charcoal', 'moss', 'dark-harbor', 'caribbean-blue'],
    limited: ['bonsai', 'dark-acai', 'amethyst', 'berry-spice', 'blue-horizon', 'celery', 'creamsicle', 'deep-purple',
      'deep-royal-blue', 'espresso', 'f-ing-pink', 'fight-club-pink', 'fireside', 'hibiscus', 'lagoon', 'martini-olive',
      'optic-white', 'pop-red', 'purple-haze', 'rapid-response-pink', 'sagewater', 'sea-mist', 'smokey-taupe'],
    tri: { leon: 'assets/set/tri/leon-navy.webp', tansen: 'assets/set/tri/tansen-navy.webp' },
    picks: MENS_WEAR,
    xsell: COBAKI_XSELL,
  }),
};
const params = new URLSearchParams(location.search);
export const SET = KITS[params.get('set') || document.documentElement.dataset.set] || KITS['wide-leg'];
/* Wear it with: the bundle's own jacket, else the Sydney */
export const SET_XSELL = SET.xsell || SYDNEY_XSELL;

/* The page says which product it is (<html data-product>); every module
   reads the product from here. */
const PAGES = { isabel: ISABEL, set: SET };
export const PRODUCT = PAGES[document.documentElement.dataset.product] || CATARINA;

/* Availability — both prototypes: Navy 6XL is sold out in every type /
   length, so it opens the waitlist (Figma 1126:87087) instead of the bag. */
export const isSoldOut = (color, size) => color === 'navy' && size === '6XL';
/* …and on the set page (2026-10-08): Large is sold out in every colour, for
   every product in the bundle, in every type / length (kit.js). */
export const isKitSoldOut = (color, size) => size === 'L';

/* WEAR IT WITH — the Isabel PDP's cross-sell (Figma 877:194945 · drawer
   1448:28827): the four core scrub tops, live names, prices and fits. The
   ghost is always the pant's colour: wearfigs.com's no-model render where the
   site has one (the eleven core colours), otherwise that top's graphite
   render recoloured to the swatch — assets/xsell/<top>/<slug>.webp. The bag
   shows `model` instead: the top on model in that colour (the Catarina PDP's
   card photo; wearfigs.com's own shot, else its navy shot recoloured). */
const top = (id, name, short, price, types) => ({
  id, name, shortName: short, plainName: name.replace('™', ''), price, sizes: APPAREL_11, types,
  typeLabel: 'Type', fitTip: 'Model is 5’8” and is wearing an X-Small',
  image: (slug) => `assets/xsell/${id}/${slug}.webp`,
  model: (slug) => (id === 'catarina' ? `assets/catarina/${slug}/card.webp` : `assets/xsell/${id}/model/${slug}.webp`),
});
export const CORE_TOPS = [
  top('catarina', 'Catarina™ One-Pocket Scrub Top', 'Catarina', 42, ['Regular', 'Petite']),
  top('casma', 'Casma™ Three-Pocket Scrub Top', 'Casma', 42, ['Regular']),
  top('rafaela', 'Rafaela™ Oversized Scrub Top', 'Rafaela', 42, ['Regular']),
  top('montex', 'Montex Scrub Top', 'Montex', 48, ['Regular']),
];

/* Shop the Look — "Head to Toe with Catarina". `image` / `fit` are the
   Figma card assets and their per-card crop; `gallery` feeds Quick Buy. */
export const LOOK = [
  {
    id: 'isabel',
    name: 'Isabel High-Rise Wide-Leg Scrub Pant',
    shortName: 'Isabel Wide-Leg',
    color: 'navy',
    price: 58,
    url: './pants.html?color=navy', // the Isabel PDP prototype
    image: 'assets/figma/stl-pant.webp',
    fit: 'cover',
    gallery: look('isabel-navy', 4),
    sizes: APPAREL_7,
    types: ['Regular', 'Petite', 'Tall'],
    colors: {
      core: ['black', 'navy', 'graphite', 'ceil-blue', 'royal-blue', 'burgundy', 'caribbean-blue', 'mauve'].map((s) => swatch(s)),
      limited: ['charcoal', 'moss', 'dark-harbor', 'purple-haze', 'fireside', 'deep-reef', 'lagoon', 'pulmonary-pink', 'shocking-pink'].map((s) => swatch(s)),
    },
  },
  {
    id: 'palmer',
    name: 'Palmer Ribbed Long-Sleeve Underscrub',
    shortName: 'Palmer Underscrub',
    color: 'black',
    price: 42,
    url: 'https://www.wearfigs.com/products/womens-ribbed-longsleeve-underscrub?color=Black',
    image: 'assets/figma/stl-underscrub.webp',
    fit: 'contain',
    gallery: look('palmer-black', 4),
    sizes: APPAREL_7,
    types: ['Regular'],
    colors: {
      core: ['black', 'navy', 'ceil-blue', 'royal-blue', 'burgundy', 'caribbean-blue', 'mauve'].map((s) => swatch(s)),
      limited: ['charcoal', 'moss', 'forest-green', 'pulmonary-pink', 'purple-haze', 'white'].map((s) => swatch(s)),
    },
  },
  {
    id: 'contourknit',
    name: 'On-Shift ContourKnit Cropped Jacket™',
    shortName: 'ContourKnit Jacket',
    color: 'navy',
    price: 98,
    url: 'https://www.wearfigs.com/products/womens-on-shift-contourknit-cropped-jacket',
    image: 'assets/figma/stl-jacket.webp',
    fit: 'contain',
    gallery: ['assets/figma/stl-jacket.webp'],
    galleryOnWhite: true,
    sizes: APPAREL_7,
    types: ['Regular'],
    colors: {
      core: ['navy', 'black'].map((s) => swatch(s)),
      limited: [],
    },
  },
  NB327_LOOK,
];

/* Model Selector — Figma 877:185201. A demo: choosing a model marks the card
   but doesn't change the photography. Names, sizes and stats are the Figma
   cards' (the mobile card's three lines, with the height marks the right way
   round); the portraits are the Figma card images. */
export const MODELS = [
  { id: 'elizabeth', name: 'Elizabeth', size: 'Small', image: 'assets/figma/model-1.webp' },
  { id: 'beck', name: 'Beck', size: 'Medium', image: 'assets/figma/model-2.webp' },
  { id: 'heather', name: 'Heather', size: 'Large', image: 'assets/figma/model-3.webp' },
  { id: 'winslow', name: 'Winslow', size: '2XL', image: 'assets/figma/model-4.webp' },
  { id: 'katrina', name: 'Katrina', size: '3XL', image: 'assets/figma/model-5.webp' },
].map((m) => ({ ...m, details: ['Height: 5’3”', 'Weight: 150 lbs', 'Pants Size: S Petite'] }));

/* Picks For You — copy and imagery exactly as the Figma "Products - Carousel". */
export const PICKS = [
  { name: 'HW Livingston', colorName: 'Black', price: '$58.00', image: 'assets/figma/picks-1.webp' },
  { name: 'W Livingston', colorName: 'Burgundy', price: '$58.00', image: 'assets/figma/picks-2.webp' },
  { name: 'HW Livingston', colorName: 'Caribbean Blue', price: '$58.00', image: 'assets/figma/picks-3.webp' },
  { name: 'HW Livingston', colorName: 'Charcoal', price: '$58.00', image: 'assets/figma/picks-4.webp' },
  { name: 'HW Livingston', colorName: 'Dark Harbor', price: '$58.00', image: 'assets/figma/picks-5.webp' },
];

export const money = (n) => `$${Number(n).toFixed(2)}`;
export const moneyShort = (n) => `$${Number(n) % 1 ? Number(n).toFixed(2) : Number(n)}`;
